import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from '../../hooks/useAudio';
import JobCard from '../../components/JobCard';
import Spinner from '../../components/Spinner';
import useResponsive from '../../hooks/useResponsive';
import { getWithExpiry, setWithExpiry, removeItem } from '../../utils/sessionStorage';
import { saveInterview } from '../../api/ApplicationService';
import { transcribeVideoOrAudio } from '../../utils/transcriptionUtils';
import { isUserSafari } from '../../utils/safariUtils';
import ToastMessage, { ToastMessageProps } from '../../components/ToastMessage';
import { useInternetConnection } from '../../utils/connection';
import { S3UploadMetadata } from '../../types/file';
import {
  PERMISSION_REQUEST_MESSAGES,
  PERMISSION_BLOCKED_MESSAGES,
  DEVICE_NOT_FOUND_MESSAGES,
  DEVICE_DISCONNECTED_MESSAGES,
  GENERIC_ERROR_MESSAGES,
  getPermissionErrorMessage,
} from '../../constants/permissionMessages';
import { pushScreenerStage, pushPermissionResult, triggerHiddenGtmClick, pushExitIntent } from '../../utils/analytics';
import useExitIntentTrigger from '../../hooks/useExitIntentTrigger';
import ExitIntentModal from '../../components/ExitIntentModal';
import { markInteractedThisSession } from '../../utils/exitIntentStorage';
import { scheduleReminder } from '../../api/ReminderService';
import { EXIT_INTENT_INTERVIEW_TIMINGS } from '../../utils/exitIntentConfig';
import {
  logScreenerRecordingRetaken,
  logScreenerCompleted,
  logScreenerPermission,
  SCREENER_STARTED_AT_KEY,
} from '../../utils/statsig';
import { getDeviceType } from '../../utils/getDeviceType';
import { useScreenerAbandonTracker } from '../../hooks/useScreenerAbandonTracker';
import { useExperiment } from '@statsig/react-bindings';
import { TakeRecord } from '../../types/interview';
import PickYourAnswer from '../../components/PickYourAnswer';
import AudioPlayback from '../../components/AudioPlayback';
import CountdownOverlay from '../../components/CountdownOverlay';
import { AnswerTimerBadge, TimerToggle } from '../../components/InterviewTimer';
import { saveTake, getTakesForQuestion, deleteTakesForQuestion, sweepExpiredTakes } from '../../utils/recordingStorage';
import { submitTake, SubmitStatus as SubmitStatusValue } from '../../utils/submitTake';
import SubmitStatus from '../../components/SubmitStatus';
import JobDetailsModal from '../../components/JobDetailsModal';
import { isNewInterviewPageEnabled } from '../../utils/featureFlags';

const MAX_RECORDING_TRIES = 3;
// VI-368 (resume): see Interview.tsx — restored takes live in IndexedDB for 24h
// to match the session TTL.
const TAKE_TTL_MS = 24 * 60 * 60 * 1000;
/*
  Audio Interview
 */
const AudioInterview = () => {
  const navigate = useNavigate();
  const isMobile = useResponsive('md');
  const isSafari = isUserSafari();
  const { 
    isAudioActive, 
    audioLevel, 
    startAudio,
    stopAudio, 
    startStreamedAudioRecording,
    prepareStreamedAudioRecording,
    startPreparedAudioRecording,
    cancelPreparedAudioSession,
    stopStreamedAudioRecording,
    recordedAudioUrl: hookRecordedAudioUrl,
    recordedBlob: hookRecordedBlob,
    recordingError,
    isRecording: isHookRecording,
    setIsRecording: setIsHookRecording,
    audioSilenceError,
    error: audioError,
    checkingValidAudioDevice,
    setRecordingError,
    setError: setAudioError,
    checkSelectedMicDeviceAvailable,
    audioStream: audioPreviewStream,
    takeFailure,
    getActiveSocket,
    isFinalizingTake,
  } = useAudio();
  
  const [isLoading, setIsLoading] = useState(true);
  // const [isHookRecording, setIsHookRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [remainingTries, setRemainingTries] = useState(3);
  const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(hookRecordedAudioUrl);
  // VI-594: when the user arrives via the Continue / Restart Interview
  // modal on the V2 landing page, that page writes `resumeFromIndex` to
  // sessionStorage so we initialize the recording flow at the right
  // question. Reading via getWithExpiry returns null when the key is
  // absent (fresh start / direct nav) so we fall back to 0.
  const initialQuestionIndex = (getWithExpiry('resumeFromIndex') as number | null) ?? 0;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(initialQuestionIndex);
  // VI-594: resumeFromIndex is a one-shot pointer. Clearing it on mount
  // means a page refresh after the initial jump falls back to question 0
  // (existing pre-VI-594 behavior). Fully cross-refresh resume would
  // require fetching upstream answers on Interview mount too; that's a
  // separate enhancement.
  useEffect(() => {
    removeItem('resumeFromIndex');
  }, []);
  const [job, setJob] = useState<any>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const hasStoppedRef = useRef(false); // Prevent multiple stops
  const [recordingTime, setRecordingTime] = useState(0);
  // VI-370: optional answer-timer visualization. Off by default; persisted so a
  // mid-interview refresh keeps the jobseeker's choice (same 24h TTL as takes).
  const [showTimer, setShowTimer] = useState<boolean>(() => getWithExpiry('showAnswerTimer') === true);
  const toggleTimer = () => {
    setShowTimer(prev => {
      const next = !prev;
      setWithExpiry('showAnswerTimer', next, TAKE_TTL_MS);
      return next;
    });
  };
  const [isReRecording, setIsReRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // VI-368: multi-take "Pick Your Answer" — gated by the SAME Statsig experiment
  // as video so both flows flip together (plus the VITE_FORCE_PICK_ANSWER dev override).
  const experiment = useExperiment('choosing_final_response_during_video_interview');
  const canChooseFinalResponse =
    import.meta.env.VITE_FORCE_PICK_ANSWER === 'true' ||
    (experiment.get('can_choose_final_response', false) as boolean);
  const [takes, setTakes] = useState<TakeRecord[]>([]);
  const [selectedTakeIndex, setSelectedTakeIndex] = useState(-1);

  // VI-368: 3-2-1 countdown before recording (parity with video). The stream +
  // WebSocket are pre-warmed during the countdown so recording starts instantly.
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // blobs
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);

  const [audioLinks, setAudioLinks] = useState<Record<string, string>>({});

  // asnyc loading state for buttons
  // VI-900: progress of the in-flight submit, shown under the Submit button.
  const [submitStatus, setSubmitStatus] = useState<SubmitStatusValue | null>(null);
  const [submitStartedAt, setSubmitStartedAt] = useState<number | null>(null);
  const [processingButtons, setProcessingButtons] = useState<{[key: string]: boolean}>({
    start: false,
    stop: false,
    reRecord: false,
    submit: false,
  });

  const handleButtonClick = async (buttonId: string, action: () => Promise<void>) => {
    const startTime = new Date();
    try {
      console.log(buttonId, ' Starting button action', new Date().toISOString());
      setProcessingButtons(prev => ({ ...prev, [buttonId]: true }));
      console.log(buttonId, ' Button action started', new Date().toISOString());
      await action();
      console.log(buttonId, ' Button action completed', new Date().toISOString());
    } finally {
      console.log(buttonId, ' Button action finally', new Date().toISOString());
      console.log("Time taken:", new Date().getTime() - startTime.getTime(), "ms");
      setProcessingButtons(prev => ({ ...prev, [buttonId]: false }));
    }
  };


  const applicantGuid = getWithExpiry('applicantGuid') || undefined;
  const jobId = getWithExpiry('jobId') || undefined;
  const interviewId = getWithExpiry('interviewId') || undefined;
  const companyId = getWithExpiry('companyId') || undefined;

  // s3
  const s3MetaData: S3UploadMetadata = {
    interviewId: interviewId || '',
    dateInterviewed: new Date().toISOString(),
    companyId: companyId || '',
  };

  // Audio refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);

  // tts
  const ttsAudio = getWithExpiry('ttsAudio') || undefined;
  const isTTSPlayedRef = useRef(false);
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);

  
  // Questions
  const questionnaires = getWithExpiry('questionnaires');
  const totalQuestions = questionnaires.length;
  const currentQuestion = questionnaires[currentQuestionIndex];
  const [currentRecordingQuestion, setCurrentRecordingQuestion] = useState<number | null>(null);

  // errors
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessageProps>({
    hasTitle: true,
    title: '',
    message: '',
    type: 'info',
    isMobile: isMobile,
  });
  
  // Track microphone permission state
  const [microphonePermissionGranted, setMicrophonePermissionGranted] = useState(false);

  // recording error
  useEffect(() => {
    if (recordingError || audioError) {
      const errorMessage = recordingError || audioError;
      console.log(errorMessage)
      const errorConfig = GENERIC_ERROR_MESSAGES.general_device_error;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: errorMessage?.replace(/\(\d+\)$/, '') || errorConfig.message,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
      setIsHookRecording(false);
      // VI-431: failures DURING a recording are handled by the takeFailure effect
      // below — a partial recording is never offered for submission.
    }
  }, [recordingError, audioError]);

  // VI-431: a recording attempt failed (connection drop, recorder or mic error,
  // connect timeout, server error). Tell the candidate and put them back where
  // they can re-record. The failed attempt is never kept as a take or made
  // submittable, and it doesn't consume a try — the Stop path that decrements
  // tries (stopRecording) never runs for it.
  useEffect(() => {
    if (!takeFailure) return;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setIsCountingDown(false);
    setCountdownValue(3);
    setRecordingTime(0);
    // Multi-take keeps any earlier good takes (and their selection) to choose
    // from; otherwise go back to the Start state — a re-record is required.
    if (!(canChooseFinalResponse && takes.length > 0)) {
      setRecordedAudioUrl(null);
      setHasRecorded(false);
      // End the recording's silence-detection loop so a stale "Are you talking?"
      // can't replace this toast. Restart the level meter unless the mic itself
      // failed — re-probing a revoked/missing mic here would set a sticky error
      // that blocks Start; the next Start re-acquires it instead.
      stopAudio();
      if (takeFailure.reason !== 'device') startAudio();
    } else {
      // Back to reviewing the earlier takes — same as after a normal Stop.
      stopAudio();
    }
    setToast({
      hasTitle: true,
      title: GENERIC_ERROR_MESSAGES.recording_failed.title,
      message: takeFailure.message,
      type: 'error',
      isMobile: isMobile,
      isOpen: true,
    });
    setShowToast(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeFailure]);

  // audio recording error handling
  useEffect(() => {
    if(audioSilenceError){
      const errorConfig = GENERIC_ERROR_MESSAGES.audio_silence;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: audioSilenceError.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    } else if (toast.title === GENERIC_ERROR_MESSAGES.audio_silence.title) {
      // VI-431: only dismiss the silence warning, never e.g. a recording-failed toast.
      setShowToast(false);
    }
  }, [audioSilenceError]);

  // general error
  useEffect(() => {
    if (error) {
      setToast({
        hasTitle: true,
        title: 'Error',
        message: error.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }
    else if (toast.title === 'Error') {
      // VI-900: only dismiss this effect's own toast — never e.g. "Submission Failed".
      setShowToast(false);
    }
  }, [error]);

  // VI-900: closing the toast also clears the page error (mirrors Interview.tsx),
  // so a stale error can't later hide or block a newer toast.
  const handleToastClose = () => {
    setShowToast(false);
    setError(null);
  };

  //internet connection
  const isConnected = useInternetConnection();

  const audioIsLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const exitIntentSuspended =
    isLoading ||
    !isConnected ||
    // VI-592: only show the interview modal after the first answer is saved.
    // currentQuestionIndex only advances past 0 once an answer is submitted
    // (and resumed sessions start at the saved count), so < 1 means nothing
    // has been saved yet.
    currentQuestionIndex < 1 ||
    // VI-900: never mid-submit — the modal claims responses were saved.
    processingButtons.submit;

  const {
    isOpen: showExitIntentModal,
    triggerSource: exitIntentTriggerSource,
    dismiss: dismissExitIntent,
  } = useExitIntentTrigger({
    page: 'interview',
    applicantGuid: applicantGuid || '',
    mouseLeaveDelayMs: EXIT_INTENT_INTERVIEW_TIMINGS.mouseLeaveDelayMs,
    idleSeconds: EXIT_INTENT_INTERVIEW_TIMINGS.idleSeconds,
    suspended: exitIntentSuspended,
  });

  const hasReportedExitIntentShown = useRef(false);
  useEffect(() => {
    if (showExitIntentModal && exitIntentTriggerSource && !hasReportedExitIntentShown.current) {
      hasReportedExitIntentShown.current = true;
      pushExitIntent({
        page: 'interview',
        trigger: exitIntentTriggerSource,
        action: 'shown',
        applicant_guid: applicantGuid || '',
        job_id: jobId || '',
      });
    }
    if (!showExitIntentModal) {
      hasReportedExitIntentShown.current = false;
    }
  }, [showExitIntentModal, exitIntentTriggerSource, applicantGuid, jobId]);

  const handleExitIntentContinueInterview = () => {
    pushExitIntent({
      page: 'interview',
      trigger: exitIntentTriggerSource ?? 'idle',
      action: 'continue_interview',
      applicant_guid: applicantGuid || '',
      job_id: jobId || '',
    });
    markInteractedThisSession(applicantGuid || '', 'interview');
    dismissExitIntent();
  };

  const handleExitIntentRemindMeLater = () => {
    const trigger = exitIntentTriggerSource ?? 'idle';
    pushExitIntent({
      page: 'interview',
      trigger,
      action: 'remind_me_later',
      applicant_guid: applicantGuid || '',
      job_id: jobId || '',
    });
    markInteractedThisSession(applicantGuid || '', 'interview');
    if (applicantGuid) {
      scheduleReminder({
        applicantGuid,
        page: 'interview',
        trigger,
      }).catch((error: unknown) => {
        console.error('[VI-548] scheduleReminder failed', error);
      });
    }
    dismissExitIntent();
  };

  useEffect(() => {
    if (!isConnected) {
      const errorConfig = GENERIC_ERROR_MESSAGES.connection_lost;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: errorConfig.message,
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    } else {
      // VI-431: only dismiss the offline notice, never e.g. a recording-failed toast.
      if (toast.title === GENERIC_ERROR_MESSAGES.connection_lost.title) setShowToast(false);
      setError(null);
    }
  }, [isConnected]);


  // Load job info
  useEffect(() => {
    setJob(getWithExpiry('job'));
    setIsLoading(false);
  }, [jobId, navigate]);

  const hasPushedStage = useRef(false);
  useEffect(() => {
    if (!hasPushedStage.current) {
      hasPushedStage.current = true;
      pushScreenerStage({
        stage_name: 'first_question',
        job_id: jobId || '',
        applicant_guid: applicantGuid || '',
        screener_question_count: totalQuestions,
      });
    }
  }, []);

  // VI-368: set when takes are restored on mount, so the mic-monitoring start
  // below is skipped (we're reviewing restored takes, not recording).
  const skipMountMicRef = useRef(false);

  // VI-368 (resume): restore takes recorded for THIS question before a refresh/
  // disconnect. Mirrors Interview.tsx. The landing page already lands the user on
  // the right question; this restores the unsubmitted takes within it.
  useEffect(() => {
    if (!canChooseFinalResponse || !applicantGuid) return;
    let cancelled = false;
    (async () => {
      try {
        await sweepExpiredTakes(TAKE_TTL_MS);
        const stored = await getTakesForQuestion(applicantGuid, initialQuestionIndex);
        if (cancelled || stored.length === 0) return;
        skipMountMicRef.current = true;
        stopAudio(); // stop level-monitoring so the mic light isn't on while reviewing
        const restored: TakeRecord[] = stored.map(r => ({
          // ws absent → submit uses the HTTP re-upload path
          blob: r.blob,
          url: URL.createObjectURL(r.blob),
          thumbnail: r.thumbnail,
          duration: r.duration,
          mediaType: r.mediaType,
          sessionId: r.id,
        }));
        setTakes(restored);
        setSelectedTakeIndex(restored.length - 1);
        setRemainingTries(Math.max(0, MAX_RECORDING_TRIES - restored.length));
        setHasRecorded(true);
      } catch (err) {
        console.warn('[VI-368] Failed to restore audio takes', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize microphone and request permissions when component mounts
  useEffect(() => {
    const initializeMediaAccess = async () => {
      let permissionStateBeforeRequest: PermissionState | null = null;
      try {
        // Clear stale device IDs from previous sessions/permission cycles
        // Browsers (Chrome/Edge) may regenerate device IDs after permission toggle
        removeItem('selectedAudioDeviceId');
        console.log('🧹 Cleared stale audio device ID from session storage');
        
        console.log('🎤 AudioInterview Mount: Requesting microphone permission...');

        // GA: Check permission state before requesting microphone
        try {
          const permStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          permissionStateBeforeRequest = permStatus.state;
          if (permStatus.state === 'prompt') {
            pushPermissionResult({ permission_granted: 'deferred', applicant_guid: applicantGuid || '' });
            triggerHiddenGtmClick('btn-askpermission-click');
            logScreenerPermission({ result: 'prompt', permissionType: 'microphone', applicantGuid: applicantGuid || '' });
          } else if (permStatus.state === 'denied') {
            pushPermissionResult({ permission_granted: false, applicant_guid: applicantGuid || '' });
            triggerHiddenGtmClick('btn-block-click');
            logScreenerPermission({ result: 'block', permissionType: 'microphone', applicantGuid: applicantGuid || '' });
          }
        } catch {
          // permissions.query may not be supported in all browsers
        }

        // Request microphone permission
        // This triggers the browser's native permission dialog
        const permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });

        console.log('✅ AudioInterview: Permission granted for microphone');

        // GA: Track permission granted
        if (permissionStateBeforeRequest !== 'granted') {
          pushPermissionResult({ permission_granted: true, applicant_guid: applicantGuid || '' });
          triggerHiddenGtmClick('btn-allow-click');
          logScreenerPermission({ result: 'allow', permissionType: 'microphone', applicantGuid: applicantGuid || '' });
        }
        
        // Capture and store the fresh microphone device ID
        const audioTrack = permissionStream.getAudioTracks()[0];
        if (audioTrack) {
          const audioSettings = audioTrack.getSettings();
          if (audioSettings.deviceId) {
            console.log('🎤 AudioInterview Mount: Storing fresh microphone device ID:', audioSettings.deviceId);
            setWithExpiry('selectedAudioDeviceId', audioSettings.deviceId, 24 * 60 * 60 * 1000);
          }
        }
        
        // Stop the permission check stream immediately
        permissionStream.getTracks().forEach(track => {
          track.stop();
          console.log(`Stopped ${track.kind} track from permission check`);
        });
        
        // Mark permission as granted
        setMicrophonePermissionGranted(true);
        
        // Now start the microphone for audio level monitoring (NOT recording)
        // This only starts the audio level visualization, recording starts when user clicks "Start Recording"
        // VI-368: skip if we're restoring takes (review mode) — keep the mic off.
        if (!isAudioActive && !skipMountMicRef.current) {
          console.log('🎤 AudioInterview: Starting microphone for audio level monitoring...');
          await startAudio();
          console.log('✅ AudioInterview: Microphone started for monitoring');
        }
      } catch (error: any) {
        console.error('❌ AudioInterview: Failed to get microphone access:', error);
        setMicrophonePermissionGranted(false);

        // GA: Track permission denied (only if not already tracked from 'denied' state)
        if (permissionStateBeforeRequest !== 'denied') {
          pushPermissionResult({ permission_granted: false, applicant_guid: applicantGuid || '' });
          triggerHiddenGtmClick('btn-block-click');
          logScreenerPermission({ result: 'block', permissionType: 'microphone', applicantGuid: applicantGuid || '' });
        }

        const errorConfig = getPermissionErrorMessage(error, 'microphone');
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
      }
    };

    initializeMediaAccess();
  }, []); // Empty dependency array - runs once on mount

  useEffect(() => {
    setRecordedAudioUrl(hookRecordedAudioUrl);
  }, [hookRecordedAudioUrl]);
  
  useEffect(() => {
    isTTSPlayedRef.current = false;
  }, [currentQuestionIndex]);

  // Add this useEffect to play TTS audio when question changes
  useEffect(() => {
    if(isTTSPlayedRef.current) return;
    // Clean up previous audio if exists
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }
    
    if (ttsAudio && Array.isArray(ttsAudio) && ttsAudio.length > currentQuestionIndex) {
      // Create a URL that the browser can access by converting file paths to API endpoints
      const audioPath = ttsAudio[currentQuestionIndex];
      // Extract just the filename from the path
      const filename = audioPath.split('/').pop() || audioPath.split('\\').pop();
      
      // Create a proper audio URL - this must match your server route exactly
      const audioUrl = `/api/v1/media/audio/stream/${encodeURIComponent(filename)}`;
      
      console.log(`Playing TTS audio for question ${currentQuestionIndex} from: ${audioUrl}`);
      
      // Create a new audio element with the HTTP URL
      const audioElement = new Audio(audioUrl);
      questionAudioRef.current = audioElement;
      
      // Play the audio automatically
      audioElement.play().catch(error => {
        console.error('Failed to play TTS audio:', error);
      });
      
      // Add event listener for debugging
      audioElement.addEventListener('ended', () => {
        console.log('TTS audio playback finished');
      });
    }
    else {
      console.log("No TTS audio found or invalid index");
    }
    
    // Cleanup function
    return () => {
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
        questionAudioRef.current.src = '';
      }
    };
  }, [currentQuestionIndex]);

  // Handle timer
  useEffect(() => {
    if (isHookRecording && !startTime) {
      const now = Date.now();
      setStartTime(now);
      // VI-370: reset elapsed at the exact start so the optional timer badge
      // always begins at 2:00 — even if a prior take ended via an error path
      // that flipped recording off without the normal stop handler's reset.
      setRecordingTime(0);
      hasStoppedRef.current = false;

      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - now) / 1000);
        setRecordingTime(elapsed);
        
        // Hard stop at 120 seconds
        if (elapsed >= 120 && !hasStoppedRef.current) {
          hasStoppedRef.current = true;
          console.log('Timer auto-stop triggered at:', elapsed, 'seconds');
          
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          
          // Force stop recording immediately
          stopRecording();
        }
      }, 1000);
    } else if (!isHookRecording) {
      // Reset when not recording
      setStartTime(null);
      hasStoppedRef.current = false;
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isHookRecording]);

  // VI-368: clear the countdown overlay the moment recording actually begins.
  useEffect(() => {
    if (isHookRecording && isCountingDown) {
      setIsCountingDown(false);
      setCountdownValue(3);
    }
  }, [isHookRecording]);

  // VI-368: if an error toast fires mid-countdown, drop the overlay so it's visible.
  useEffect(() => {
    if (showToast && isCountingDown && !isHookRecording) {
      setIsCountingDown(false);
      setCountdownValue(3);
    }
  }, [showToast]);

  // VI-368: clean up the countdown timer on unmount.
  useEffect(() => {
    return () => { if (countdownTimerRef.current) clearInterval(countdownTimerRef.current); };
  }, []);

  // Start recording function
  const startRecording = async (isRerecord = false) => {
    try {
      setError(null);
      setRecordingError(null);
      setAudioError(null);

      if(!isConnected){
        const errorConfig = GENERIC_ERROR_MESSAGES.connection_lost;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }

      if(recordingError || audioError){
        const errorConfig = GENERIC_ERROR_MESSAGES.recording_error;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: recordingError || audioError || errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }
      
      // Step 0: Check if selected microphone device is still available (before permission checks)
      // This prevents falling back to default device when user's selected device is disconnected
      const micDeviceCheck = await checkSelectedMicDeviceAvailable();
      if (!micDeviceCheck.available) {
        console.log('🎤 Selected microphone device is not available');
        const errorConfig = DEVICE_DISCONNECTED_MESSAGES.microphone;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return; // Don't proceed - user needs to reconnect device
      }

      // Always verify microphone permission before starting recording
      // This ensures we catch cases where user revoked permissions after initial grant
      let micPermissionOk = false;
      
      // Safari-specific: Check if the existing audio monitoring stream was revoked.
      // Safari doesn't support navigator.permissions.query() and may still return a valid
      // stream from getUserMedia even after the user denies mic in website settings.
      // The most reliable signal is whether the monitoring stream's tracks are still alive.
      if (isUserSafari() && audioPreviewStream) {
        const previewAudioTrack = audioPreviewStream.getAudioTracks()[0];
        if (previewAudioTrack && (previewAudioTrack.readyState !== 'live' || previewAudioTrack.muted)) {
          console.log('🎤 Safari: Audio monitoring track is dead/muted - mic access was revoked');
          const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
          setMicrophonePermissionGranted(false);
          return;
        }
      }
      
      try {
        console.log('🎤 Checking microphone permission state...');
        
        // Check if permissions API is supported for microphone
        let micPermissionState: PermissionState | null = null;
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          micPermissionState = micPermission.state;
          console.log('🎤 Microphone permission state:', micPermissionState);
        } catch (permQueryError) {
          console.log('🎤 Permissions API not supported for microphone, will check via getUserMedia');
        }
        
        // If we know permission is denied, show toast FIRST
        if (micPermissionState === 'denied') {
          console.log('🎤 Microphone permission is blocked, showing toast first...');
          const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
          setToast({
            hasTitle: true,
            title: errorConfig.title,
            message: errorConfig.message,
            type: 'error',
            isMobile: isMobile,
            isOpen: true,
          });
          setShowToast(true);
          setMicrophonePermissionGranted(false);
          
          // Safari: getUserMedia may auto-succeed from session cache even when denied,
          // bypassing the user's denial without showing a visible prompt. Just return.
          if (isUserSafari()) {
            console.log('🎤 Safari: Permission denied, not retrying getUserMedia (session cache bypass)');
            return;
          }
          
          // On other browsers, attempt to re-request permission (triggers visible prompt)
          try {
            console.log('🎤 Attempting to re-request microphone permission...');
            const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            permissionStream.getTracks().forEach(track => track.stop());
            micPermissionOk = true;
            setMicrophonePermissionGranted(true);
            console.log('✅ Microphone permission granted after re-request');
          } catch (e) {
            console.error('❌ Microphone permission still denied after re-request');
            return; // Don't proceed with recording
          }
        } else {
          // Permission is 'granted', 'prompt', or unknown (permissions API not supported)
          // Try to get access - if it fails with NotAllowedError, show toast first then retry
          try {
            const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Validate stream is actually live (Safari may return dead/muted tracks when device is disabled at OS level)
            const micTrack = permissionStream.getAudioTracks()[0];
            if (!micTrack || micTrack.readyState !== 'live' || micTrack.muted) {
              console.log('🎤 Mic stream returned dead/muted track (likely disabled at OS level)');
              permissionStream.getTracks().forEach(track => track.stop());
              throw new Error('Microphone track is not live');
            }
            
            // Safari may initially report track as live but it can go dead shortly after
            if (isUserSafari()) {
              await new Promise(resolve => setTimeout(resolve, 300));
              if (micTrack.readyState !== 'live' || micTrack.muted) {
                console.log('🎤 Mic track became dead/muted after Safari delay check');
                permissionStream.getTracks().forEach(track => track.stop());
                throw new Error('Microphone track is not live');
              }
            }
            
            permissionStream.getTracks().forEach(track => track.stop());
            micPermissionOk = true;
            setMicrophonePermissionGranted(true);
            console.log('✅ Microphone permission verified');
          } catch (getUserMediaError: any) {
            if (getUserMediaError?.name === 'NotAllowedError') {
              // Permission was denied - show toast FIRST
              console.log('🎤 Microphone access denied, showing toast first...');
              const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
              setToast({
                hasTitle: true,
                title: errorConfig.title,
                message: errorConfig.message,
                type: 'error',
                isMobile: isMobile,
                isOpen: true,
              });
              setShowToast(true);
              setMicrophonePermissionGranted(false);
              
              // Safari: getUserMedia may auto-succeed from session cache. Just return.
              if (isUserSafari()) {
                console.log('🎤 Safari: Permission denied, not retrying getUserMedia');
                return;
              }
              
              // On other browsers, attempt to re-request after showing toast
              try {
                console.log('🎤 Attempting to re-request microphone permission...');
                const retryStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                retryStream.getTracks().forEach(track => track.stop());
                micPermissionOk = true;
                setMicrophonePermissionGranted(true);
                console.log('✅ Microphone permission granted after re-request');
              } catch (retryError) {
                console.error('❌ Microphone permission still denied after re-request');
                return; // Don't proceed with recording
              }
            } else {
              // Other error (device not found, dead stream, etc.)
              throw getUserMediaError;
            }
          }
        }
      } catch (micError: any) {
        console.error('❌ Microphone permission check/request failed:', micError);
        setMicrophonePermissionGranted(false);
        const errorConfig = PERMISSION_BLOCKED_MESSAGES.microphone;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return; // Don't proceed with recording
      }
      
      if (!micPermissionOk) {
        return;
      }
      
      const isAudioDeviceValid = await checkingValidAudioDevice();
      if(!isAudioDeviceValid){
        console.error("Invalid audio device, stopping recording");
        return;
      }

      if(isRerecord){
        setIsReRecording(true);
        setRecordedAudioUrl(null);
      }
      // mute when forced to record
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
        questionAudioRef.current.src = '';
      }
      await startAudio();
      chunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      // Set which question we're recording for
      setCurrentRecordingQuestion(currentQuestionIndex);
      
      // Start the streamed recording (multi-take group passes onTakeReady to collect takes)
      await startStreamedAudioRecording(
        applicantGuid,
        currentQuestionIndex,
        currentQuestion.questionText,
        currentQuestionIndex === totalQuestions - 1,
        totalQuestions,
        s3MetaData,
        canChooseFinalResponse ? handleTakeReady : undefined,
      );
      
      setIsHookRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      if(isRerecord){
        setIsReRecording(false);
      }
    } catch (error) {
      console.error('Error starting recording:', error);

      setIsHookRecording(false);
    }
  };
  
  // Stop recording function
  const stopRecording = async () => {
    // await new Promise(resolve => setTimeout(resolve, 2000));
    stopStreamedAudioRecording();
    stopAudio();
    setRecordingTime(0);
    
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    
    setIsHookRecording(false);
    setHasRecorded(true);
    setRemainingTries(prev => prev - 1);
    
    // Clear recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };
  
  // Play the recorded audio
  const playRecording = () => {
    try{
      if (audioRef.current && recordedAudioUrl) {
        console.log("Playing from recordedAudioUrl:", recordedAudioUrl);
        audioRef.current.src = recordedAudioUrl;
        audioRef.current.muted = false;
        audioRef.current.volume = 1.0;
        audioRef.current.play().catch(e => {
          console.error('Audio playback error:', e);
          setError(`Unable to play audio. (${Date.now()})`);
        });
      } else {
        setError(`Unable to play audio. (${Date.now()})`);
      }
    }catch(error){
      console.error('Error playing recording:', error);
      setError(`Unable to play audio. (${Date.now()})`);
    }
  };

  const muteAudio = () => {
    try{
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      
      if (audioRef.current && recordedAudioUrl) { 
        audioRef.current.muted = newMutedState;
        audioRef.current.play().catch(e => setError(`Unable to mute audio. (${Date.now()})`));
  
        // cleanup
        const onEnded = () => {
          audioRef.current?.removeEventListener('ended', onEnded);
        };
  
        audioRef.current.addEventListener('ended', onEnded);
      }
    }catch(error){
      console.error('Error muting audio:', error);
      setError(`Unable to mute audio. (${Date.now()})`);
    }
    
  };
  
  // Format time for display
  const formatTimeRemaining = () => {
    const secondsRemaining = 120 - recordingTime;
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const reRecord = async () => {
    setIsReRecording(true);
    // await new Promise(resolve => setTimeout(resolve, 2000));
    if (remainingTries > 0) {
      
      // Reset UI state
      // setHasRecorded(false);
      setRecordedAudioUrl(null);
      
      // Start a new recording for this question
      startRecording();
      
      console.log(`Re-recording question ${currentQuestionIndex}`);
    }
    setIsReRecording(false);
  };

  // VI-368: start the 3-2-1 countdown, pre-warming the stream + WebSocket in
  // parallel so recording begins with zero latency at 0. Mirrors Interview.tsx.
  const startCountdown = () => {
    if (!isConnected || recordingError || audioError) {
      const errorConfig = (recordingError || audioError)
        ? GENERIC_ERROR_MESSAGES.recording_error
        : GENERIC_ERROR_MESSAGES.connection_lost;
      setToast({
        hasTitle: true,
        title: errorConfig.title,
        message: (recordingError || audioError)?.replace(/\(\d+\)$/, '') || errorConfig.message,
        type: 'error',
        isMobile,
        isOpen: true,
      });
      setShowToast(true);
      return;
    }
    if (remainingTries === 0) {
      setError(GENERIC_ERROR_MESSAGES.no_tries_remaining.message);
      return;
    }

    // Mute TTS before recording
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }
    // VI-702: stop any review playback so the recorded audio doesn't keep playing
    // after clicking Rerecord (the control-flow <audio> element stays mounted).
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Ensure mic monitoring is on for the level viz + silence detection
    if (!isAudioActive) {
      startAudio();
    }

    setIsReRecording(false);
    setIsCountingDown(true);
    setCountdownValue(3);

    const preparePromise = prepareStreamedAudioRecording(
      applicantGuid,
      currentQuestionIndex,
      currentQuestion.questionText,
      currentQuestionIndex === totalQuestions - 1,
      totalQuestions,
      s3MetaData,
      canChooseFinalResponse ? handleTakeReady : undefined,
    );

    let count = 3;
    countdownTimerRef.current = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownValue(count);
      } else {
        clearInterval(countdownTimerRef.current!);
        countdownTimerRef.current = null;
        preparePromise.then(ready => {
          if (ready) startPreparedAudioRecording();
        }).catch(() => {
          setIsCountingDown(false);
          setCountdownValue(3);
        });
      }
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    cancelPreparedAudioSession();
    setIsCountingDown(false);
    setCountdownValue(3);
    // remainingTries is NOT decremented — cancelling the countdown is free
  };

  const handleRerecordClick = () => {
    // VI-431: never start a new attempt while the current answer is being
    // submitted, or before the previous take has been delivered.
    if (processingButtons.submit || isFinalizingTake) return;
    if (!canChooseFinalResponse) {
      // Single-take: the new recording replaces this one, so release its socket
      // (no finalize signal → the server discards it) instead of leaving its
      // FFmpeg process open. Submit falls back to HTTP if the countdown is cancelled.
      const previousSocket = getActiveSocket();
      if (previousSocket && previousSocket.readyState === WebSocket.OPEN) previousSocket.close();
    }
    const attemptNumber = MAX_RECORDING_TRIES - remainingTries + 1;
    logScreenerRecordingRetaken({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: getWithExpiry('applicantGuid') || undefined,
      deviceType: getDeviceType(),
      questionNumber: currentQuestionIndex + 1,
      attemptNumber,
    });
    startCountdown();
  };
  
  // VI-368: fired by useAudio's recorder.onstop once a take's blob is ready.
  // Mirrors Interview.tsx — collects the take and persists it to IndexedDB so a
  // refresh/disconnect can restore it. Best-effort save; never blocks recording.
  const handleTakeReady = ({ ws, blob, url, thumbnail, duration }: {
    ws: WebSocket; blob: Blob; url: string; thumbnail: string; duration: number;
  }) => {
    const takeIndex = takes.length;
    const sessionId = applicantGuid ? `${applicantGuid}_q${currentQuestionIndex}_take${takeIndex}` : undefined;
    const newTake: TakeRecord = { ws, blob, url, thumbnail, duration, mediaType: 'audio', sessionId };
    setTakes(prev => [...prev, newTake]);
    setSelectedTakeIndex(takeIndex);

    if (applicantGuid) {
      saveTake({
        applicantGuid,
        questionIndex: currentQuestionIndex,
        takeIndex,
        mediaType: 'audio',
        blob,
        thumbnail,
        duration,
        recordedAt: Date.now(),
        questionText: currentQuestion.questionText,
        s3MetaData,
      }).catch(err => console.warn('[VI-368] saveTake (audio) failed', err));
    }
  };

  const handleTakeSelect = (index: number) => {
    if (processingButtons.submit) return; // VI-431: don't switch takes mid-submit
    setSelectedTakeIndex(index);
  };

  // Navigate to next question or finish
  const fireScreenerCompleted = () => {
    const startedAtRaw = getWithExpiry(SCREENER_STARTED_AT_KEY);
    const startedAt = typeof startedAtRaw === 'number'
      ? startedAtRaw
      : typeof startedAtRaw === 'string'
        ? parseInt(startedAtRaw, 10)
        : null;
    const totalTimeSeconds = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;

    logScreenerCompleted({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: getWithExpiry('applicantGuid') || undefined,
      deviceType: getDeviceType(),
      totalQuestions,
      totalTimeSeconds,
    });
    setIsInterviewCompleted(true);
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questionnaires.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setHasRecorded(false);
      setRecordedAudioUrl(null);
      setRemainingTries(3);
    } else {
      // Last question - complete interview
      fireScreenerCompleted();
      navigate('/v1/interview/success');
    }
  };

  const submitStreamRecording = async () => {
    try{
      // mute playback audio
      if(audioRef.current){
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

      // VI-431: resolve the take to submit. The multi-take flow submits the
      // selected take; the single-take flow submits the hook's last recording on
      // its socket. A failed attempt is never a take and clears the hook's blob,
      // so there is nothing to submit after a failure until a successful re-record.
      const finalIndex = canChooseFinalResponse
        ? (selectedTakeIndex >= 0 && selectedTakeIndex < takes.length ? selectedTakeIndex : takes.length - 1)
        : -1;
      const finalTake = canChooseFinalResponse
        ? takes[finalIndex]
        : hookRecordedBlob
          ? { ws: getActiveSocket(), blob: hookRecordedBlob }
          : null;

      if (!finalTake) {
        const errorConfig = GENERIC_ERROR_MESSAGES.recording_failed;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }

      setSubmitStartedAt(Date.now());
      setSubmitStatus({ phase: 'saving' });
      try {
        // Resolves only once the server has taken ownership of the answer — the
        // ack of process_recording on the live socket (it then finishes saving
        // even if the connection drops), or a successful HTTP re-upload of the
        // local copy (restored take, or the socket dropped during review).
        await submitTake(finalTake, {
          mode: 'audio',
          applicantGuid: applicantGuid || '',
          questionId: currentQuestionIndex,
          questionText: currentQuestion.questionText,
          isLastQuestion,
          totalQuestions,
          s3MetaData,
        }, setSubmitStatus);
      } catch (err) {
        console.error('[VI-431] Audio answer submission failed', err);
        const errorConfig = GENERIC_ERROR_MESSAGES.submit_failed;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return; // stay on this question with takes intact so they can retry
      } finally {
        setSubmitStatus(null);
        setSubmitStartedAt(null);
      }

      if (canChooseFinalResponse) {
        // VI-368: discard the non-selected takes. Closing their sockets without
        // the finalize signal makes the server drop them.
        takes.forEach((take, i) => {
          if (i !== finalIndex && take.ws && take.ws.readyState !== WebSocket.CLOSED) {
            take.ws.close();
          }
        });
        takes.forEach((take, i) => {
          if (i !== finalIndex) URL.revokeObjectURL(take.url);
        });
        if (applicantGuid) {
          deleteTakesForQuestion(applicantGuid, currentQuestionIndex).catch(() => {});
        }
      }

      // Navigate or finalize
      if (!isLastQuestion) {
        setHasRecorded(false);
        setRemainingTries(3);
        takes.forEach(take => URL.revokeObjectURL(take.url));
        setTakes([]);
        setSelectedTakeIndex(-1);
        setCurrentQuestionIndex(prev => prev + 1);

        // Restart microphone for next question
        if (!isAudioActive) {
          console.log('🎤 Restarting microphone for next question...');
          setTimeout(async () => {
            await startAudio();
          }, 500); // Small delay to ensure smooth transition
        }
      }else{
        fireScreenerCompleted();
        navigate('/v1/interview/success');
      }
    }catch(error){
      console.error('Error submitting recording:', error);
      setError(GENERIC_ERROR_MESSAGES.recording_error.message);
      throw error;
    }

  };

  useScreenerAbandonTracker({
    lastStep: 'questions',
    questionNumber: currentQuestionIndex + 1,
    isCompleted: isInterviewCompleted,
  });

  // Submit recording
  const submitRecording = async () => {
    try{
      if(!isConnected){
        const errorConfig = GENERIC_ERROR_MESSAGES.connection_lost;
        setToast({
          hasTitle: true,
          title: errorConfig.title,
          message: errorConfig.message,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }
      if (!hasRecorded) {
        console.warn('Cannot submit: No recording available');
        return;
      }
      
      // Call the stream submission handler
      await submitStreamRecording();
    }catch(error){
      console.error('Error submitting recording:', error);
      setError(GENERIC_ERROR_MESSAGES.recording_error.message);
    }
    
  };

  // for cleanup
  useEffect(() => {
    return () => {
      // Stop recording if it's still going
      if (mediaRecorderRef.current && isHookRecording) {
        mediaRecorderRef.current.stop();
      }
      
      // Stop and clean up audio stream
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      
      // Release any created object URLs — BUT only for the legacy single-take
      // flow. In the multi-take (Pick Your Answer) flow, each take's URL is the
      // same object URL as recordedAudioUrl; revoking it here when a re-record
      // sets a new recordedAudioUrl kills the PREVIOUS take's playback
      // (ERR_FILE_NOT_FOUND). The takes[] flow owns those URLs and revokes them
      // on submit/advance instead.
      if (!canChooseFinalResponse) {
        if (recordedUrl) {
          URL.revokeObjectURL(recordedUrl);
        }
        if (recordedAudioUrl && recordedAudioUrl !== recordedUrl) {
          URL.revokeObjectURL(recordedAudioUrl);
        }
      }
    };
  }, [isHookRecording, recordedUrl, recordedAudioUrl, hookRecordedAudioUrl, canChooseFinalResponse]);

  // VI-370 / VI-820 (AC #8): icon-only Show/Hide Timer toggle, inline with the
  // recording controls. Only on the 1st try — hidden on the 2nd/3rd try per
  // question (remainingTries drops below the max after the first stop). The timer
  // visualization itself carries over via `showTimer`.
  const timerToggle = remainingTries === MAX_RECORDING_TRIES ? (
    <TimerToggle showTimer={showTimer} onToggle={toggleTimer} isMobile={isMobile} />
  ) : null;

  // VI-834: view-only state/derivations for the redesigned (flag-on) layout.
  const newLayout = isNewInterviewPageEnabled();
  const [showJobDetails, setShowJobDetails] = useState(false);
  // VI-834 mobile: collapsible question bottom sheet (see Interview.tsx).
  const [questionExpanded, setQuestionExpanded] = useState(false);
  const applicantFirstName = (getWithExpiry('applicantFirstName') as string) || '';
  const companyName = job?.companyName || '';
  const jobTitle = job?.title || '';
  const jobLocation = job?.locations?.[0]
    ? [job.locations[0].city, job.locations[0].region, job.locations[0].country]
        .filter(Boolean)
        .join(', ')
    : '';
  const isAudioReviewing = hasRecorded && !isHookRecording && !isCountingDown;
  // VI-431: Submit needs a successful recording to submit — the selected take
  // (multi-take) or the last recording (single-take). This also covers the gap
  // between Stop and the take being delivered by recorder.onstop.
  const hasValidTake = !isFinalizingTake && (canChooseFinalResponse ? takes.length > 0 : !!hookRecordedBlob);
  const canSubmitAnswer = isAudioReviewing && hasValidTake;

  // VI-834: NOTE — the redesigned page intentionally does NOT post IFRAME_HEIGHT.
  // It fills the host-allocated iframe height (root minHeight:100vh + full-bleed
  // grey); posting scrollHeight shrank the iframe to content and exposed the
  // host's white background below it.

  // ===========================================================================
  // VI-834 — Redesigned (flag-on) jobseeker AUDIO recording layout. Same shell as
  // the video page; the player swaps in the existing mic/waveform visualizer.
  // View layer only — every ref/handler/id from the logic above is preserved.
  // ===========================================================================
  if (newLayout) {
    const POPPINS = "'Poppins', sans-serif";
    const isRecordingPhase = isHookRecording || isCountingDown;

    const audioPlayer = (
      <div
        style={{
          position: 'relative',
          width: '100%',
          // Desktop keeps the video card's aspect; on mobile that aspect is only
          // ~185px tall — too short for the mic + waveform (they overflowed/clipped),
          // so the audio card (no video frame) gets a taller, DEFINITE height. It
          // must be a fixed height (not min-height) so AudioPlayback's percentage
          // heights (100% / waveform 50% / gradient 70%) resolve — otherwise the
          // playback collapsed to 0 on mobile.
          aspectRatio: isMobile ? undefined : '768 / 396',
          height: isMobile ? '320px' : undefined,
          background: '#e3e2e4',
          borderRadius: '8px',
          border: '1.607px solid #adb5bd',
          overflow: 'hidden',
          boxShadow:
            '0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <audio ref={audioRef} src={recordedAudioUrl || ''} style={{ display: 'none' }} />

        {isCountingDown && <CountdownOverlay value={countdownValue} />}
        {showTimer && !isCountingDown && (isHookRecording || !hasRecorded) && (
          <AnswerTimerBadge recordingTime={recordingTime} isMobile={isMobile} isRecording={isHookRecording} />
        )}

        {isHookRecording ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: isMobile ? '1.5rem' : '2.5rem',
              padding: '16px',
              boxSizing: 'border-box',
            }}
          >
            <AudioMic level={audioLevel} isRecording={isHookRecording} />
            <AudioWaveform level={audioLevel} isMobile={isMobile} />
          </div>
        ) : isAudioReviewing ? (
          // VI-846: native browser playback (like the video's VideoPlayback) for
          // every review state — replaces the old custom play/mute over a gradient.
          <AudioPlayback
            src={(canChooseFinalResponse && takes.length > 0)
              ? (takes[selectedTakeIndex >= 0 ? selectedTakeIndex : takes.length - 1]?.url ?? recordedAudioUrl)
              : recordedAudioUrl}
          />
        ) : !isCountingDown ? (
          <div style={{ textAlign: 'center' }}>
            <img src="/svgs/mic.svg" alt="Microphone" style={{ width: '64px', height: '64px', opacity: 0.5 }} />
            <p style={{ color: '#585b62', marginTop: '1rem', fontFamily: POPPINS, fontSize: isMobile ? '0.85rem' : '0.95rem' }}>
              Click &ldquo;Start Recording&rdquo; to begin
            </p>
          </div>
        ) : null}
      </div>
    );

    // VI-834: circular-arrows glyph reused by Re-Record (blue) + tries text (grey).
    const refreshIcon = (color: string, size = 16) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <path d="M21 3v6h-6" />
      </svg>
    );

    const triesText = (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontFamily: POPPINS,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          color: '#6c757d',
          whiteSpace: 'nowrap',
        }}
      >
        {refreshIcon('#6c757d', 14)}
        {remainingTries} {remainingTries === 1 ? 'Try' : 'Tries'} Available
      </span>
    );

    const startStopButton = !isRecordingPhase ? (
      // Figma: Start is an OUTLINE button — white bg, brand-blue border/text,
      // leading blue record dot (NOT a filled button).
      <button
        onClick={startCountdown}
        disabled={processingButtons.start}
        id="btn-startrecord-click"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '40px',
          padding: '0 20px',
          background: '#eaf3fc',
          color: '#2f86dd',
          border: '1px solid #2f86dd',
          borderRadius: '8px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: processingButtons.start ? 'wait' : 'pointer',
        }}
      >
        {processingButtons.start ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#2f86dd', flexShrink: 0 }} />
            Start Recording
          </>
        )}
      </button>
    ) : (
      <button
        onClick={() => {
          if (isCountingDown) {
            cancelCountdown();
          } else {
            handleButtonClick('stop', async () => { await stopRecording(); });
          }
        }}
        disabled={processingButtons.stop && !isCountingDown}
        id="btn-stoprecord-click"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          height: '40px',
          padding: '0 24px',
          background: '#dc2626',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: 'pointer',
        }}
      >
        {processingButtons.stop && !isCountingDown ? (
          <Spinner size="sm" color="light" />
        ) : (
          <>
            <span style={{ width: '14px', height: '14px', background: '#ffffff', borderRadius: '2px', flexShrink: 0 }} />
            {isCountingDown ? 'Stop' : 'Stop Recording'}
          </>
        )}
      </button>
    );

    const assistanceText = (
      <p style={{ margin: 0, fontFamily: POPPINS, fontSize: '12px', lineHeight: '18px', color: '#6c757d' }}>
        If you need any assistance with this process, please reach out to{' '}
        <a
          href="mailto:jobseeker-support@jobtarget.com"
          id="btn-jobseekersupport-click"
          style={{ color: '#2f86dd', textDecoration: 'none' }}
        >
          Job Seeker Support
        </a>
        .
      </p>
    );

    // Re-Record lives in the control row (Figma), matching Start/Stop height (40).
    const reRecordButton = (
      <button
        onClick={handleRerecordClick}
        disabled={remainingTries === 0 || processingButtons.reRecord || processingButtons.submit || isFinalizingTake}
        id="btn-rerecord-click"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '40px',
          padding: '0 20px',
          background: '#eaf3fc',
          color: '#2f86dd',
          border: '1px solid #2f86dd',
          borderRadius: '8px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
          opacity: remainingTries === 0 ? 0.5 : 1,
        }}
      >
        {processingButtons.reRecord ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#2f86dd', flexShrink: 0 }} />
            Re-Record
          </>
        )}
      </button>
    );

    const submitButton = (
      <button
        onClick={() => handleButtonClick('submit', async () => { await submitRecording(); })}
        disabled={!canSubmitAnswer || processingButtons.submit}
        aria-busy={processingButtons.submit}
        id="submit-interview-answer-button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '48px',
          padding: '0 32px',
          background: '#2f86dd',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          fontFamily: POPPINS,
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          cursor: canSubmitAnswer && !processingButtons.submit ? 'pointer' : 'not-allowed',
          opacity: canSubmitAnswer ? 1 : 0.5,
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {processingButtons.submit ? <Spinner size="sm" color="light" label="Submitting your answer" /> : (audioIsLastQuestion ? 'Submit & Finish' : 'Submit & Continue')}
      </button>
    );

    const leftButton = isAudioReviewing ? reRecordButton : startStopButton;

    return (
      // Figma: desktop canvas is grey (#e9ecef); mobile canvas is white.
      <div style={{ background: isMobile ? '#ffffff' : '#e9ecef', minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
        {toast && (
          <ToastMessage {...toast} isOpen={showToast} onClose={handleToastClose} />
        )}

        {isLoading ? (
          <div style={{ flex: '1 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Spinner size={isMobile ? 'lg' : 'xl'} />
          </div>
        ) : (
          <>
            {/* Content capped + centered; canvas behind stays full-bleed. Mobile
                is a flex column so the question card drops to the bottom (Figma
                bottom-sheet), just above the submit bar. */}
            <div
              style={{
                flex: '1 0 auto',
                width: '100%',
                maxWidth: '1240px',
                margin: '0 auto',
                boxSizing: 'border-box',
                // Mobile leaves room for the fixed question sheet + submit bar.
                padding: isMobile ? '16px 16px 260px' : '32px 50px',
              }}
            >
              {/* Title block — Figma desktop gap is 0 (line-height only); mobile 8. */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '8px' : '0px',
                  marginBottom: isMobile ? '24px' : '32px',
                }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontFamily: POPPINS,
                    fontWeight: 600,
                    fontSize: isMobile ? '20px' : '23px',
                    lineHeight: isMobile ? '28px' : '32px',
                    color: '#020305',
                  }}
                >
                  {applicantFirstName ? `Hi ${applicantFirstName}! ` : ''}Welcome to your interview
                  {companyName ? <> with {companyName.toUpperCase()}</> : ''}
                </h1>
                {(jobTitle || jobLocation) && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: POPPINS,
                      fontSize: '14px',
                      lineHeight: '26px',
                    }}
                  >
                    {jobTitle && <span style={{ color: '#272a2f' }}>{jobTitle}</span>}
                    {jobTitle && jobLocation && <span style={{ color: '#6c757d' }}>•</span>}
                    {/* Mobile stacks the location onto its own line (Figma). */}
                    {jobLocation && <span style={{ color: '#6c757d', flexBasis: isMobile ? '100%' : 'auto' }}>{jobLocation}</span>}
                  </div>
                )}
                {job && (
                  <button
                    onClick={() => setShowJobDetails(true)}
                    id="btn-showmore-click"
                    style={{
                      alignSelf: 'flex-start',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: POPPINS,
                      fontWeight: 500,
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: '#0045a1',
                      textDecoration: 'underline',
                    }}
                  >
                    Show More
                  </button>
                )}
              </div>

              {/* Two columns */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '24px' : '40px',
                  alignItems: isMobile ? 'stretch' : 'flex-start',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                }}
              >
                {/* Left — audio player + controls */}
                <div
                  style={{
                    flex: isMobile ? 'none' : '1 1 0',
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '100%' : '660px',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                  }}
                >
                  {audioPlayer}

                  {/* Control row (Figma). Desktop: button + tries left, timer
                      right. Mobile: button + timer on one row, tries below.
                      Audio has no blur toggle. */}
                  {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        {leftButton}
                        <TimerToggle showTimer={showTimer} onToggle={toggleTimer} isMobile={isMobile} redesign />
                      </div>
                      {!isRecordingPhase && triesText}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {leftButton}
                        {!isRecordingPhase && triesText}
                      </div>
                      <TimerToggle showTimer={showTimer} onToggle={toggleTimer} isMobile={isMobile} redesign />
                    </div>
                  )}

                  {canChooseFinalResponse && takes.length > 0 && (
                    <PickYourAnswer
                      takes={takes}
                      selectedIndex={selectedTakeIndex}
                      onSelect={handleTakeSelect}
                      isMobile={isMobile}
                    />
                  )}
                </div>

                {/* Right — question card (DESKTOP only; mobile uses the fixed
                    bottom sheet rendered below). */}
                {!isMobile && (
                <div
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    padding: '24px 32px',
                    flex: '0 0 440px',
                    width: '440px',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    alignSelf: 'flex-start',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 8px',
                          background: '#f4f5f9',
                          border: '1px solid #e9ecef',
                          borderRadius: '4px',
                          fontFamily: POPPINS,
                          fontSize: '12px',
                          fontWeight: 500,
                          lineHeight: '16px',
                          letterSpacing: '0.24px',
                          color: '#585b62',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Question {currentQuestionIndex + 1} of {totalQuestions}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontFamily: POPPINS,
                          fontSize: '12px',
                          fontWeight: 500,
                          lineHeight: '16px',
                          letterSpacing: '0.24px',
                          color: '#a4a7ac',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <i className="far fa-clock" style={{ fontSize: '16px' }} />
                        2 mins limit
                      </span>
                    </div>
                    <h2
                      style={{
                        margin: 0,
                        fontFamily: POPPINS,
                        fontWeight: 600,
                        fontSize: isMobile ? '16px' : '23px',
                        lineHeight: isMobile ? '24px' : '32px',
                        color: '#323235',
                      }}
                    >
                      {currentQuestion?.questionText}
                    </h2>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Desktop bottom bar — assistance (left) + Submit & Continue (right). */}
            {!isMobile && (
            <div style={{ borderTop: '1px solid #d9dade', background: '#ffffff', width: '100%' }}>
              <div
                style={{
                  maxWidth: '1240px',
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '16px 50px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SubmitStatus status={submitStatus} startedAt={submitStartedAt} />
                  {assistanceText}
                </div>
                {submitButton}
              </div>
            </div>
            )}

            {/* Mobile: fixed bottom sheet — collapsible question panel above the
                submit bar (slides up over the controls on tap; X collapses). */}
            {isMobile && (
              <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60, display: 'flex', flexDirection: 'column' }}>
                <div
                  onClick={() => { if (!questionExpanded) setQuestionExpanded(true); }}
                  style={{
                    background: '#ffffff',
                    borderTop: '1px solid #e9ecef',
                    borderLeft: '1px solid #e9ecef',
                    borderRight: '1px solid #e9ecef',
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                    boxShadow: '0 -8px 28px rgba(13,45,77,0.14), 0 -2px 4px rgba(173,181,189,0.25)',
                    padding: '12px 24px',
                    cursor: questionExpanded ? 'default' : 'pointer',
                    maxHeight: questionExpanded ? '55vh' : '88px',
                    overflowY: questionExpanded ? 'auto' : 'hidden',
                    transition: 'max-height 0.3s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                    <span
                      style={{
                        display: 'inline-flex', alignItems: 'center', padding: '4px 8px',
                        background: '#f4f5f9', border: '1px solid #e9ecef', borderRadius: '4px',
                        fontFamily: POPPINS, fontSize: '12px', fontWeight: 500, lineHeight: '16px',
                        letterSpacing: '0.24px', color: '#585b62', whiteSpace: 'nowrap',
                      }}
                    >
                      Question {currentQuestionIndex + 1} of {totalQuestions}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: POPPINS,
                          fontSize: '12px', fontWeight: 500, lineHeight: '16px', letterSpacing: '0.24px',
                          color: '#a4a7ac', whiteSpace: 'nowrap',
                        }}
                      >
                        <i className="far fa-clock" style={{ fontSize: '16px' }} />
                        2 mins limit
                      </span>
                      {questionExpanded && (
                        <button
                          type="button"
                          aria-label="Collapse question"
                          onClick={(e) => { e.stopPropagation(); setQuestionExpanded(false); }}
                          style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: '#585b62', display: 'inline-flex', lineHeight: 0 }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <h2
                    style={{
                      margin: 0,
                      fontFamily: POPPINS,
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#323235',
                      ...(questionExpanded
                        ? {}
                        : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                    }}
                  >
                    {currentQuestion?.questionText}
                  </h2>
                </div>
                <div style={{ background: '#ffffff', borderTop: '1px solid #d9dade', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {submitButton}
                  <SubmitStatus status={submitStatus} startedAt={submitStartedAt} />
                  {assistanceText}
                </div>
              </div>
            )}
          </>
        )}

        {job && (
          <JobDetailsModal
            isOpen={showJobDetails}
            onClose={() => setShowJobDetails(false)}
            job={job}
          />
        )}

        <ExitIntentModal
          isOpen={showExitIntentModal}
          title="Continue your interview now?"
          body="Your responses have been saved automatically. You can finish now or come back later."
          primaryLabel="Continue Interview"
          secondaryLabel="Remind me Later"
          primaryId="btn-exitintent-continue-interview-click"
          secondaryId="btn-exitintent-remind-me-later-click"
          onPrimary={handleExitIntentContinueInterview}
          onSecondary={handleExitIntentRemindMeLater}
        />

        {/* Hidden GTM permission click triggers (unchanged) */}
        <button id="btn-allow-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-block-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-askpermission-click" style={{ display: 'none' }} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: isMobile ? '1rem' : '2rem',
        paddingTop: '0',
      }}
    >
      {toast && (
        <ToastMessage
          {...toast}
          isOpen={showToast}
          onClose={handleToastClose}
        />
      )}
      <div
        style={{
          width: isMobile ? '100%' : '70%',
          textAlign: 'left',
          padding: '0',
          borderRadius: '8px',
          marginTop: '0',
        }}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : job ? (
          <JobCard job={job} isMobile={isMobile} />
        ) : (
          <div>No job information available</div>
        )}

        {/* Main content area - two columns on desktop, stacked on mobile */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '2rem',
              minHeight: 'calc(100vh - 400px)', 
            }}
          >
            {/* Left Column - Question */}
            <div
              style={{
                flex: isMobile ? '1' : '1',
              }}
            >
              {/* Question Counter */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#555', fontWeight: '500' }}>
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </p>
              </div>

              {/* Question Text with Back Button */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  marginBottom: isMobile ? '1.2rem' : '1.5rem',
                }}
              >
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '500',
                    color: '#333',
                    margin: '0',
                  }}
                >
                  "{currentQuestion.questionText}"
                </h2>
              </div>
              <span style={{
                fontSize: '0.75rem',
                color: '#6c757d',
                margin: '0',
                marginLeft: 'auto',
                marginTop: '0.2rem',
              }}>
                Maximum Answer Length: 2 Minutes
            </span>

              {/* VI-368: Pick Your Answer — same component/layout as video; audio
                  takes use a built-in placeholder thumbnail. */}
              {canChooseFinalResponse && takes.length > 0 && (
                <PickYourAnswer
                  takes={takes}
                  selectedIndex={selectedTakeIndex}
                  onSelect={handleTakeSelect}
                  isMobile={isMobile}
                />
              )}
            </div>

            {/* Right Column - Audio Waveform and Controls */}
            <div style={{ flex: isMobile ? '1' : '1' }}>
              {/* Audio Waveform with Border */}
              <div
                style={{
                  width: '100%',
                  aspectRatio: '4/3',
                  position: 'relative',
                  borderRadius: '8px',
                  // VI-368: border removed — the dark thumbnail/playback met the
                  // light border with a harsh edge. The rounded, overflow-hidden
                  // box on a subtle background reads cleaner.
                  marginBottom: '1rem',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Audio playback element */}
                <audio ref={audioRef} src={recordedAudioUrl || ''} style={{ display: 'none' }} />

                {/* VI-368: countdown overlay during the 3-2-1 */}
                {isCountingDown && (
                  <CountdownOverlay value={countdownValue} />
                )}

                {/* VI-370: optional answer timer. Visible in the "upcoming
                    recording" state (static 02:00, so jobseekers can preview it)
                    and while recording (counting down). Hidden during the 3-2-1
                    countdown overlay and while reviewing a recorded take. */}
                {showTimer && !isCountingDown && (isHookRecording || !hasRecorded) && (
                  <AnswerTimerBadge recordingTime={recordingTime} isMobile={isMobile} isRecording={isHookRecording} />
                )}

                {isHookRecording ? (
                  <div style={{ 
                    width: '100%', 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '15px',
                    marginTop: '5.0rem',
                    gap: isMobile ? '3.0rem' : '5.0rem',
                    maxHeight: '350px',  // Control max height
                  }}>
                    <AudioMic level={audioLevel} isRecording={isHookRecording} />
                    <AudioWaveform level={audioLevel} isMobile={isMobile} />
                  </div>
                ) : (hasRecorded && !isHookRecording && !isCountingDown && canChooseFinalResponse && takes.length > 0) ? (
                  // VI-368: multi-take review — play the selected take.
                  <AudioPlayback
                    src={takes[selectedTakeIndex >= 0 ? selectedTakeIndex : takes.length - 1]?.url ?? recordedAudioUrl}
                  />
                ) : hasRecorded && !isHookRecording && !isCountingDown ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <div style={{ 
                      width: '80%', 
                      height: '50%',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}>
                      <img src="/svgs/waveform-gray.svg" alt="Audio waveform" style={{ width: '100%', height: '100%' }} />
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '70%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 100%)',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        padding: '0 20px 20px 20px',
                      }}
                    >
                      {/* Play buttons after recording */}
                      <>
                        <button
                          onClick={playRecording}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/svgs/play-button.svg"
                            alt="Play"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>

                        <button
                          onClick={muteAudio}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/svgs/audio-replay.svg"
                            alt="Audio"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>
                      </>
                    </div>
                  </div>
                ) : !isCountingDown ? (
                  <div style={{ textAlign: 'center' }}>
                    <img src="/svgs/mic.svg" alt="Microphone" style={{ width: '64px', height: '64px', opacity: 0.5 }} />
                    <p style={{ color: '#6c757d', marginTop: '1rem', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>Click "Start Recording" to begin</p>
                  </div>
                ) : null}
              </div>

              {/* Recording Controls */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  // VI-820: wrap instead of overflowing on narrow (Android) widths
                  // so no control needs horizontal scrolling to reach.
                  flexWrap: 'wrap',
                  gap: isMobile ? '0.5rem' : '1rem',
                  marginBottom: '1rem',
                }}
              >
                {/* Show different controls based on recording state */}
                {!isHookRecording && !isCountingDown && !hasRecorded && !isReRecording ? (
                  <>
                    <button
                      onClick={startCountdown}
                      id="btn-startrecord-click"
                      style={{
                        padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1.5rem',
                        backgroundColor: '#0D6EFD',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        cursor: 'pointer',
                      }}
                    >
                      {processingButtons.start ? <Spinner size="sm" color="light" /> : 'Start Recording'}
                    </button>

                    <div
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '14px',
                        color: '#6c757d',
                      }}
                    >
                      {remainingTries} tries left
                    </div>
                    {timerToggle}
                  </>
                ) : (isHookRecording || isCountingDown) ? (
                  <>
                    <button
                      onClick={() => {
                        if (isCountingDown) {
                          cancelCountdown();
                        } else {
                          handleButtonClick('stop', async () => { await stopRecording(); });
                        }
                      }}
                      disabled={processingButtons.stop && !isCountingDown}
                      id="btn-stoprecord-click"
                      style={{
                        // VI-702: match Figma — narrower stop button (aligned with video flow)
                        padding: isMobile ? '0.4rem 0.6rem' : '0.3rem 0.6rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        cursor: 'pointer',
                      }}
                    >
                      {processingButtons.stop && !isCountingDown
                        ? <Spinner size="sm" color="light" />
                        : isCountingDown ? 'Stop' : 'Stop Recording'}
                    </button>
                    {
                      // VI-370: suppress the legacy last-30s text when the optional
                      // timer is on, so we never show two competing readouts.
                      !showTimer && recordingTime >= 90 && (
                        <span
                          style={{
                            padding: '0.2rem 0.8rem',
                            borderRadius: '10px',
                            fontWeight: '500',
                            fontSize: '0.85rem',
                            color: '#6c757d',
                          }}
                        >
                          {formatTimeRemaining()}
                        </span>
                      )
                    }

                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                      }}
                    >
                      {remainingTries} tries left
                    </div>
                    {timerToggle}
                  </>
                ) : isMobile ? (
                  /* Mobile layout - stacked */
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: '0.75rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <button
                          onClick={handleRerecordClick}
                          disabled={remainingTries === 0 || processingButtons.submit || isFinalizingTake}
                          id="btn-rerecord-click"
                          style={{
                            flex: '1',
                            marginRight: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: 'white',
                            color: '#333',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontWeight: '500',
                            cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                            opacity: remainingTries === 0 ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            justifyContent: 'center',
                          }}
                        >
                          {processingButtons.reRecord ? <Spinner size="sm" color="primary" /> : <><img src="/svgs/retry.svg" alt="Rerecord" /> Rerecord</>}
                        </button>

                        <button
                          onClick={() => handleButtonClick('submit', async () => {
                            await submitRecording();
                          })}
                          disabled={!canSubmitAnswer || processingButtons.submit}
                          aria-busy={processingButtons.submit}
                          id="submit-interview-answer-button"
                          style={{
                            flex: '1',
                            marginLeft: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#11807a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: '500',
                            cursor: canSubmitAnswer ? 'pointer' : 'not-allowed',
                            opacity: canSubmitAnswer ? 1 : 0.5,
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {processingButtons.submit ? <Spinner size="sm" color="light" label="Submitting your answer" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                        </button>
                      </div>

                      <div
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e9ecef',
                          borderRadius: '16px',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          textAlign: 'center',
                          width: 'fit-content',
                          margin: '0 auto',
                        }}
                      >
                        {remainingTries} tries left
                      </div>
                    </div>
                  </>
                ) : (
                  /* Desktop layout - side by side with tries left in middle */
                  <>
                    <div
                      style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%' }}
                    >
                      <button
                        onClick={handleRerecordClick}
                        disabled={remainingTries === 0 || processingButtons.submit || isFinalizingTake}
                        id="btn-rerecord-click"
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'white',
                          color: '#333',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontWeight: '500',
                          gap: '0.5rem',
                          cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                          opacity: remainingTries === 0 ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {processingButtons.reRecord ? <Spinner size="sm" color="primary" /> : <><img src="/svgs/retry.svg" alt="Rerecord" /> Rerecord</>}
                      </button>

                      <div
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e9ecef',
                          borderRadius: '16px',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          margin: '0 auto',
                        }}
                      >
                        {remainingTries} tries left
                      </div>

                      <button
                        onClick={() => handleButtonClick('submit', async () => {
                          await submitRecording();
                        })}
                        disabled={!canSubmitAnswer || processingButtons.submit}
                        aria-busy={processingButtons.submit}
                        id="submit-interview-answer-button"
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#11807a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontWeight: '500',
                          cursor: canSubmitAnswer ? 'pointer' : 'not-allowed',
                          opacity: canSubmitAnswer ? 1 : 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        {processingButtons.submit ? <Spinner size="sm" color="light" label="Submitting your answer" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* VI-900: submit progress (legacy layout, mobile + desktop) */}
              <SubmitStatus status={submitStatus} startedAt={submitStartedAt} align="center" marginTop="1rem" />

              {/* Support Message */}
              <div style={{
                textAlign: 'center',
                marginBottom: '1rem',
                marginTop: isMobile ? '1.5rem' : '1.8rem',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                color: '#6c757d',
              }}>
                Need Assistance? Please email us at <a href="mailto:jobseeker-support@jobtarget.com" id="btn-jobseekersupport-click" style={{ color: '#6c757d', textDecoration: 'underline' }}>Job Seeker Support</a>.
              </div>
            </div>
          </div>
        )}
        {/* Hidden elements for GTM permission click triggers */}
        <button id="btn-allow-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-block-click" style={{ display: 'none' }} aria-hidden="true" />
        <button id="btn-askpermission-click" style={{ display: 'none' }} aria-hidden="true" />
      </div>

      <ExitIntentModal
        isOpen={showExitIntentModal}
        title="Continue your interview now?"
        body="Your responses have been saved automatically. You can finish now or come back later."
        primaryLabel="Continue Interview"
        secondaryLabel="Remind me Later"
        primaryId="btn-exitintent-continue-interview-click"
        secondaryId="btn-exitintent-remind-me-later-click"
        onPrimary={handleExitIntentContinueInterview}
        onSecondary={handleExitIntentRemindMeLater}
      />
    </div>
  );
};

// Audio Waveform Component - reduced height and padding
const AudioWaveform = ({ level, isMobile }: { level: number, isMobile: boolean }) => {
  // Convert 0-255 audio level to 0-100 percentage
  const levelPercentage = Math.min(100, Math.max(0, level * 3.0 * 100 / 255));
  
  return (
    <div className="audio-level-container" style={{
      width: '100%',
      height: 'auto',
      backgroundColor: 'transparent',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '10px',
      marginBottom: '20px'
    }}>
      {/* Wrapper to ensure proper sizing */}
      <div style={{
        position: 'relative',
        width: isMobile ? '285px' : '391px',
        height: '45px'
      }}>
        {/* Background waveform (gray) */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: isMobile ? '285px' : '391px', height: '45px' }}>
          <img src="/svgs/waveform-gray.svg" alt="Audio waveform" style={{ width: isMobile ? '285px' : '391px', height: '45px' }} />
        </div>
        
        {/* Active blue part */}
        <div style={{ 
          position: 'absolute', 
          top: 0,
          left: 0,
          height: '45px', 
          width: `${levelPercentage}%`, 
          overflow: 'hidden' 
        }}>
          <img src="/svgs/waveform-blue.svg" alt="" style={{ width: isMobile ? '285px' : '391px', height: '45px' }} />
        </div>
      </div>
    </div>
  );
};

// AudioMic component - reduced size for better fit
const AudioMic = ({ level, isRecording }: { level: number, isRecording: boolean }) => {
  // Convert 0-255 audio level to 0-100 percentage
  const levelPercentage = Math.min(100, Math.max(0, level * 3.0 * 100 / 255));
  
  // Calculate animation values based on audio level
  const pulseScale = 1 + (levelPercentage / 100) * 0.3; // Scale between 1.0 and 1.3
  
  return (
    <div style={{
      width: '100%',
      // VI-846: the circles below are position:absolute, so with height:auto this
      // wrapper collapsed to 0 and the pulsing 120px circle (up to 1.3× = 156px)
      // overflowed onto the waveform. Reserve real height to keep them apart.
      height: '160px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      margin: '0 auto'
    }}>
      {/* Outer pulsing circle - reduced size */}
      <div style={{
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        backgroundColor: '#cbddee',
        position: 'absolute',
        transform: isRecording ? `scale(${pulseScale})` : 'scale(1)',
        transition: 'transform 0.05s linear, background-color 0.1s ease-out',
        opacity: 0.7
      }} />
      
      {/* Inner static circle - reduced size */}
      <div style={{
        width: '90px',
        height: '90px', 
        borderRadius: '50%',
        backgroundColor: '#eaf3fc',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1
      }}>
        {/* Microphone icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <img src="/svgs/micbold.svg" alt="Microphone" style={{ width: '48px', height: '48px', fontWeight: 'bold' }} />
        </div>
      </div>
      
      {/* Active outer circle overlay - only visible when recording */}
      {isRecording && levelPercentage > 5 && (
        <div style={{
          width: '120px', // Smaller size
          height: '120px', // Smaller size
          borderRadius: '50%',
          backgroundColor: '#2f86dd',
          position: 'absolute',
          transform: `scale(${pulseScale})`,
          transition: 'transform 0.2s ease',
          opacity: levelPercentage / 200 + 0.3, // Vary opacity with level
          zIndex: 0
        }} />
      )}
    </div>
  );
};

export default AudioInterview;
